import { NextPage } from 'next';
import React, { FC } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import JsonLd from '../../../components/layouts/JsonLd';
import MetaTags from '../../../components/layouts/MetaTags';
import { MerchantSite } from '../../../components/MerchantSite';
import MerchantBlogPage from '../../../templates/merchants/blogs/IndexTemplate/MerchantBlogPage';
import ErrorTemplate from '../../../templates/ErrorTemplate';
import {
  allCategoriesCache,
  getCategory,
} from '../../../infrastructure/api/backend/explore_api';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../interfaces/merchants';
import { MerchantBlogIndex } from '../../../interfaces/merchants/blogs';
import {
  fetchMerchantBlogs,
  fetchMerchantDetail,
} from '../../../infrastructure/api';
import { injectAppContextGetServerSideProps } from '../../_app';
import { usePushMerchantDataLayerEffect } from '../../../hooks/gtm';
import { buildSiteUrl } from 'src/utils/request-url';

const titleBy = (intl: IntlShape, displayName: string, currentPage: number) => {
  const base = intl.formatMessage(
    { id: 'merchants.blogs.index.meta.title' },
    { displayName },
  );
  if (currentPage <= 1) {
    return base;
  }
  return `${base} ${intl.formatMessage(
    { id: 'merchants.blogs.index.page' },
    { currentPage },
  )}`;
};

type Props = {
  pagination: {
    totalSize: number;
    currentPage: number;
  };
  blogPosts?: MerchantBlogIndex;
  publicId: string;
  merchantDetail?: MerchantDetail;
  categories: CategoryEntity[];
  category?: CategoryEntity;
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
};

const Blogs: FC<Props> = ({
  publicId,
  pagination,
  merchantDetail,
  category,
  blogPosts,
  pagination: { currentPage },
  isCoubicApp,
}) => {
  const intl = useIntl();
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchantDetail?.id });

  if (merchantDetail && category) {
    const title = titleBy(intl, merchantDetail.display_name, currentPage);

    return (
      <>
        <MetaTags
          title={title}
          description={
            merchantDetail.seo_setting?.description ||
            merchantDetail.description
          }
          keywords={merchantDetail.seo_setting?.keyword || ''}
          thumbnailUrl={merchantDetail.cover_image.url || ''}
          currentPath={buildSiteUrl(`/blogs?page=${currentPage}`)}
          noIndex={merchantDetail.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchantDetail.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchantDetail?.favicon_image?.url}
        />
        <JsonLd merchant={merchantDetail} />
        {blogPosts && (
          <MerchantBlogPage
            merchantDetail={merchantDetail}
            blogPosts={blogPosts}
            publicId={publicId}
            category={category}
            pagination={pagination}
            isCoubicApp={isCoubicApp}
          />
        )}
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantBlog: NextPage<Props> = (props: Props) => {
  const { merchantDetail, isUnpublishedSite } = props;
  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchantDetail}
      logoImage={merchantDetail?.logo_image || null}
      merchantPublicId={merchantDetail?.public_id || null}
      displayName={merchantDetail?.display_name || null}
      isCoubicApp={props.isCoubicApp}
    >
      <Blogs {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async ({ query, session, token, requestId, isCoubicApp }) => {
    const merchantPublicId = (query.publicId as string) ?? '';
    const blogPageNumber = query.page ? (query.page as string) : '1';

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          pagination: {
            totalSize: 0,
            currentPage: 1,
          },
          categories: [],
          publicId: merchantPublicId,
          merchantDetail: merchant,
          isUnpublishedSite,
          isCoubicApp,
        },
      };
    }

    const [categories, blogs] = await Promise.all([
      // Layoutで取得している部分を徐々に置き換え中
      allCategoriesCache(),
      fetchMerchantBlogs({
        session,
        token,
        requestId,
        merchantPublicId,
        blogPageNumber,
      }),
    ]).catch((err) => {
      throw err;
    });

    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;
    const propsData: Props = {
      pagination: {
        totalSize: blogs.total_size,
        currentPage: blogs.current_page,
      },
      blogPosts: blogs,
      categories,
      category,
      publicId: merchantPublicId,
      merchantDetail: merchant,
      isUnpublishedSite,
      isCoubicApp,
    };

    return { props: propsData };
  },
);

export default MerchantBlog;
